import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable, of } from 'rxjs';
import { map, tap } from 'rxjs/operators';
import { ServiceAppService } from '../../../security/service-app.service';
import { IFiltros, ITotais, ITotaisPrincipal, ITotaisHistorico } from './interface-apuracao-vdm-peca';

export interface ApuracaoVdmData {
  dados: any[];
  totais: ITotais;
  totaisPrincipal: ITotaisPrincipal;
  totaisHistorico: ITotaisHistorico;
  filtros: IFiltros;
}

@Injectable({
  providedIn: 'root'
})
export class ApuracaoVdmPecaService {
  private dataSubject = new BehaviorSubject<ApuracaoVdmData | null>(null);
  private loadingSubject = new BehaviorSubject<boolean>(false);
  private lastFiltros: IFiltros | null = null;

  public data$ = this.dataSubject.asObservable();
  public loading$ = this.loadingSubject.asObservable();

  constructor(private service: ServiceAppService) {}

  /**
   * Carrega os dados da apuração VDM
   * @param filtros Filtros aplicados
   * @param forceRefresh Força o refresh mesmo se os filtros não mudaram
   */
  public carregarDados(filtros: IFiltros, forceRefresh: boolean = false): Observable<ApuracaoVdmData> {
    // Verifica se os filtros mudaram ou se é um refresh forçado
    const filtrosMudaram = this.filtrosMudaram(filtros);
    
    if (!filtrosMudaram && !forceRefresh && this.dataSubject.value) {
      // Retorna os dados em cache se os filtros não mudaram
      return of(this.dataSubject.value);
    }

    this.loadingSubject.next(true);
    this.lastFiltros = { ...filtros };

    const url = `/apuracao_vdm_peca?dataAno=${filtros.dataAno}&selecaoVdm=${filtros.selecaoVdm}&considerarBalcao=${filtros.considerarBalcao}`;

    return this.service.getDados(url).pipe(
      map(res => this.processarDados(res, filtros)),
      tap(data => {
        this.dataSubject.next(data);
        this.loadingSubject.next(false);
      })
    );
  }

  /**
   * Processa os dados retornados da API
   */
  private processarDados(res: any, filtros: IFiltros): ApuracaoVdmData {
    const dados = res.dados || [];

    // Processa os dados para o histórico (mesma lógica do componente histórico)
    const dadosProcessados = dados.map(el => ({
      ...el,
      abrGap: el.mensal?.abrGap || 0,
      abrObjetivo: el.mensal?.abrObjetivo || 0,
      abrPercent: el.mensal?.abrPercent || 0,
      abrRealizado: el.mensal?.abrRealizado || 0,
      agoGap: el.mensal?.agoGap || 0,
      agoObjetivo: el.mensal?.agoObjetivo || 0,
      agoPercent: el.mensal?.agoPercent || 0,
      agoRealizado: el.mensal?.agoRealizado || 0,
      dezGap: el.mensal?.dezGap || 0,
      dezObjetivo: el.mensal?.dezObjetivo || 0,
      dezPercent: el.mensal?.dezPercent || 0,
      dezRealizado: el.mensal?.dezRealizado || 0,
      fevGap: el.mensal?.fevGap || 0,
      fevObjetivo: el.mensal?.fevObjetivo || 0,
      fevPercent: el.mensal?.fevPercent || 0,
      fevRealizado: el.mensal?.fevRealizado || 0,
      janGap: el.mensal?.janGap || 0,
      janObjetivo: el.mensal?.janObjetivo || 0,
      janPercent: el.mensal?.janPercent || 0,
      janRealizado: el.mensal?.janRealizado || 0,
      julGap: el.mensal?.julGap || 0,
      julObjetivo: el.mensal?.julObjetivo || 0,
      julPercent: el.mensal?.julPercent || 0,
      julRealizado: el.mensal?.julRealizado || 0,
      junGap: el.mensal?.junGap || 0,
      junObjetivo: el.mensal?.junObjetivo || 0,
      junPercent: el.mensal?.junPercent || 0,
      junRealizado: el.mensal?.junRealizado || 0,
      maiGap: el.mensal?.maiGap || 0,
      maiObjetivo: el.mensal?.maiObjetivo || 0,
      maiPercent: el.mensal?.maiPercent || 0,
      maiRealizado: el.mensal?.maiRealizado || 0,
      marGap: el.mensal?.marGap || 0,
      marObjetivo: el.mensal?.marObjetivo || 0,
      marPercent: el.mensal?.marPercent || 0,
      marRealizado: el.mensal?.marRealizado || 0,
      novGap: el.mensal?.novGap || 0,
      novObjetivo: el.mensal?.novObjetivo || 0,
      novPercent: el.mensal?.novPercent || 0,
      novRealizado: el.mensal?.novRealizado || 0,
      outGap: el.mensal?.outGap || 0,
      outObjetivo: el.mensal?.outObjetivo || 0,
      outPercent: el.mensal?.outPercent || 0,
      outRealizado: el.mensal?.outRealizado || 0,
      q1Gap: el.mensal?.q1Gap || 0,
      q1Objetivo: el.mensal?.q1Objetivo || 0,
      q1Percent: el.mensal?.q1Percent || 0,
      q1Realizado: el.mensal?.q1Realizado || 0,
      q2Gap: el.mensal?.q2Gap || 0,
      q2Objetivo: el.mensal?.q2Objetivo || 0,
      q2Percent: el.mensal?.q2Percent || 0,
      q2Realizado: el.mensal?.q2Realizado || 0,
      q3Gap: el.mensal?.q3Gap || 0,
      q3Objetivo: el.mensal?.q3Objetivo || 0,
      q3Percent: el.mensal?.q3Percent || 0,
      q3Realizado: el.mensal?.q3Realizado || 0,
      q4Gap: el.mensal?.q4Gap || 0,
      q4Objetivo: el.mensal?.q4Objetivo || 0,
      q4Percent: el.mensal?.q4Percent || 0,
      q4Realizado: el.mensal?.q4Realizado || 0,
      setGap: el.mensal?.setGap || 0,
      setObjetivo: el.mensal?.setObjetivo || 0,
      setPercent: el.mensal?.setPercent || 0,
      setRealizado: el.mensal?.setRealizado || 0,
    }));

    // Monta os totais principais
    const totais: ITotais = {
      totalBudgetFull: res.totalBudgetFull || 0,
      totalBudgetYtd: res.totalBudgetYtd || 0,
      totalPendenteFull: res.totalPendenteFull || 0,
      totalPendenteYtd: res.totalPendenteYtd || 0,
      totalValorDesconto: res.totalValorDesconto || 0,
      totalValorComDesconto: res.totalValorComDesconto || 0,
      totalRealizado: res.totalRealizado || 0
    };

    // Monta os totais do principal
    const totaisPrincipal: ITotaisPrincipal = {
      totalBudgetFull: res.totalBudgetFullMes || 0,
      totalBudgetMtd: res.totalBudgetMtd || 0,
      totalRealizado: res.totalRealizadoMes || 0,
      totalValorDesconto: res.totalValorDescontoMes || 0,
      totalValorComDesconto: res.totalValorComDescontoMes || 0,
      totalGapMesFull: res.totalGapMesFull || 0,
      totalGapMesYtd: res.totalGapMesYtd || 0
    };

    // Monta os totais do histórico
    const totaisHistorico: ITotaisHistorico = {
      filtroAno: res.filtroAno || 0,
      totalAbrGap: res.totalAbrGap || 0,
      totalAbrObjetivo: res.totalAbrObjetivo || 0,
      totalAbrPercent: res.totalAbrPercent || 0,
      totalAbrRealizado: res.totalAbrRealizado || 0,
      totalAgoGap: res.totalAgoGap || 0,
      totalAgoObjetivo: res.totalAgoObjetivo || 0,
      totalAgoPercent: res.totalAgoPercent || 0,
      totalAgoRealizado: res.totalAgoRealizado || 0,
      totalDezGap: res.totalDezGap || 0,
      totalDezObjetivo: res.totalDezObjetivo || 0,
      totalDezPercent: res.totalDezPercent || 0,
      totalDezRealizado: res.totalDezRealizado || 0,
      totalFevGap: res.totalFevGap || 0,
      totalFevObjetivo: res.totalFevObjetivo || 0,
      totalFevPercent: res.totalFevPercent || 0,
      totalFevRealizado: res.totalFevRealizado || 0,
      totalJanGap: res.totalJanGap || 0,
      totalJanObjetivo: res.totalJanObjetivo || 0,
      totalJanPercent: res.totalJanPercent || 0,
      totalJanRealizado: res.totalJanRealizado || 0,
      totalJulGap: res.totalJulGap || 0,
      totalJulObjetivo: res.totalJulObjetivo || 0,
      totalJulPercent: res.totalJulPercent || 0,
      totalJulRealizado: res.totalJulRealizado || 0,
      totalJunGap: res.totalJunGap || 0,
      totalJunObjetivo: res.totalJunObjetivo || 0,
      totalJunPercent: res.totalJunPercent || 0,
      totalJunRealizado: res.totalJunRealizado || 0,
      totalMaiGap: res.totalMaiGap || 0,
      totalMaiObjetivo: res.totalMaiObjetivo || 0,
      totalMaiPercent: res.totalMaiPercent || 0,
      totalMaiRealizado: res.totalMaiRealizado || 0,
      totalMarGap: res.totalMarGap || 0,
      totalMarObjetivo: res.totalMarObjetivo || 0,
      totalMarPercent: res.totalMarPercent || 0,
      totalMarRealizado: res.totalMarRealizado || 0,
      totalNovGap: res.totalNovGap || 0,
      totalNovObjetivo: res.totalNovObjetivo || 0,
      totalNovPercent: res.totalNovPercent || 0,
      totalNovRealizado: res.totalNovRealizado || 0,
      totalOutGap: res.totalOutGap || 0,
      totalOutObjetivo: res.totalOutObjetivo || 0,
      totalOutPercent: res.totalOutPercent || 0,
      totalOutRealizado: res.totalOutRealizado || 0,
      totalQ1Gap: res.totalQ1Gap || 0,
      totalQ1Objetivo: res.totalQ1Objetivo || 0,
      totalQ1Percent: res.totalQ1Percent || 0,
      totalQ1Realizado: res.totalQ1Realizado || 0,
      totalQ2Gap: res.totalQ2Gap || 0,
      totalQ2Objetivo: res.totalQ2Objetivo || 0,
      totalQ2Percent: res.totalQ2Percent || 0,
      totalQ2Realizado: res.totalQ2Realizado || 0,
      totalQ3Gap: res.totalQ3Gap || 0,
      totalQ3Objetivo: res.totalQ3Objetivo || 0,
      totalQ3Percent: res.totalQ3Percent || 0,
      totalQ3Realizado: res.totalQ3Realizado || 0,
      totalQ4Gap: res.totalQ4Gap || 0,
      totalQ4Objetivo: res.totalQ4Objetivo || 0,
      totalQ4Percent: res.totalQ4Percent || 0,
      totalQ4Realizado: res.totalQ4Realizado || 0,
      totalSetGap: res.totalSetGap || 0,
      totalSetObjetivo: res.totalSetObjetivo || 0,
      totalSetPercent: res.totalSetPercent || 0,
      totalSetRealizado: res.totalSetRealizado || 0
    };

    return {
      dados: dadosProcessados,
      totais,
      totaisPrincipal,
      totaisHistorico,
      filtros
    };
  }

  /**
   * Verifica se os filtros mudaram
   */
  private filtrosMudaram(novosFiltros: IFiltros): boolean {
    if (!this.lastFiltros) return true;
    
    return (
      this.lastFiltros.dataAno !== novosFiltros.dataAno ||
      this.lastFiltros.selecaoVdm !== novosFiltros.selecaoVdm ||
      this.lastFiltros.considerarBalcao !== novosFiltros.considerarBalcao
    );
  }

  /**
   * Força o refresh dos dados
   */
  public refreshDados(filtros: IFiltros): Observable<ApuracaoVdmData> {
    return this.carregarDados(filtros, true);
  }

  /**
   * Obtém os dados atuais em cache
   */
  public getDadosAtuais(): ApuracaoVdmData | null {
    return this.dataSubject.value;
  }

  /**
   * Limpa o cache de dados
   */
  public limparCache(): void {
    this.dataSubject.next(null);
    this.lastFiltros = null;
  }

  /**
   * Download do arquivo analítico
   */
  public downloadAnalitico(dealer: string, filtros: IFiltros): Observable<any> {
    const url = `/apuracao_vdm_peca/analitico?dealer=${dealer}&dataAno=${filtros.dataAno}&selecaoVdm=${filtros.selecaoVdm}&considerarBalcao=${filtros.considerarBalcao}`;
    return this.service.downloadArquivo(url);
  }
}
